export function normalizeAffiliateText(value) {
  return value.normalize("NFD").replaceAll(/[\u0300-\u036f]/g, "");
}

export function getNetworkName(tpRede) {
  switch (tpRede) {
    case "P":
      return "Principal";
    case "C":
      return "Complementar";
    case "A":
      return "MediService";
    default:
      return "";
  }
}

export function withNetworkName(item) {
  return {
    NetworkName: getNetworkName(item.TpRede),
    ...item
  };
}

export function countActiveFilters(filters) {
  return ["facilities", "languages", "networks", "subspecialties"].reduce(
    (total, key) => total + (filters?.[key]?.filter(item => !!item.isActive).length || 0),
    0
  );
}

export function getFacilities(list) {
  const facilities = [];
  if (list.some(item => !!item.Agendamento)) facilities.push({ name: "online" });
  if (list.some(item => !!item.Teleconsulta)) facilities.push({ name: "videoCall" });
  if (list.some(item => !!item.WhatsApp)) facilities.push({ name: "whatsapp" });
  return facilities;
}

function sortByNormalizedLabel(list) {
  return list.sort((a, b) => {
    if (a.normalizedLabel > b.normalizedLabel) return 1;
    if (a.normalizedLabel < b.normalizedLabel) return -1;
    return 0;
  });
}

function buildOptionList(list, sourceKey, targetKey, fallback = []) {
  const options = [];

  list.forEach(item => {
    const source = item[sourceKey] ? item[sourceKey] : fallback;
    const values = Array.isArray(source) ? source : source.split(",");

    item[targetKey] = values.map(value => {
      const normalizedLabel = normalizeAffiliateText(value).trim();
      if (!options.some(option => option.normalizedLabel === normalizedLabel)) {
        options.push({
          label: value,
          normalizedLabel
        });
      }
      return normalizedLabel;
    });
  });

  return sortByNormalizedLabel(options);
}

export function getLanguages(list) {
  return buildOptionList(list, "DsIdioma", "languages", ["Português"]);
}

export function getNetworkList(list) {
  const networks = [];

  list.forEach(item => {
    const normalizedLabel = normalizeAffiliateText(item.NetworkName).trim();
    if (!networks.some(option => option.normalizedLabel === normalizedLabel)) {
      networks.push({
        label: item.NetworkName,
        normalizedLabel
      });
    }
  });

  return sortByNormalizedLabel(networks);
}

export function getSubspecialtyList(list) {
  return buildOptionList(list, "SubEspecialidades", "subspecialties");
}

function ensureSearchFields(list, selectedLanguages) {
  if (selectedLanguages?.length && !list[0].languages) {
    getLanguages(list);
  }
}

function applyOptionFilter(list, selectedOptions, itemKey) {
  if (!selectedOptions?.length) return list;
  const selectedLabels = new Set(selectedOptions.map(option => option.normalizedLabel));
  return list.filter(item => item[itemKey].some(value => selectedLabels.has(value)));
}

function applyNetworkFilter(list, selectedNetworks) {
  if (!selectedNetworks?.length) return list;
  const networks = new Set(selectedNetworks.map(network => network.normalizedLabel));
  return list.filter(item => networks.has(item.NetworkName));
}

function applyFacilitiesFilter(list, facilities = []) {
  const facilityFilters = {
    online: item => !!item.Agendamento,
    videoCall: item => !!item.Teleconsulta,
    whatsapp: item => !!item.WhatsApp
  };

  return facilities
    .filter(facility => facility.isActive)
    .reduce((result, facility) => result.filter(facilityFilters[facility.name] ?? (() => true)), list);
}

export function filterAffiliateList(originalList, filters = {}, textSearch = "") {
  if (!originalList?.length) return [];

  const selectedLanguages = filters?.languages?.filter(language => language.isActive);
  const selectedSubspecialties = filters?.subspecialties?.filter(subspecialty => subspecialty.isActive);
  const selectedNetworks = filters?.networks?.filter(network => network.isActive);

  ensureSearchFields(originalList, selectedLanguages);

  let list = originalList;
  if (textSearch !== "") {
    const normalizedSearch = normalizeAffiliateText(textSearch).trim().toUpperCase();
    list = originalList.filter(item =>
      normalizeAffiliateText(String(item.DescricaoPrestador ?? ""))
        .toUpperCase()
        .includes(normalizedSearch)
    );
  }
  if (!list.length) return [];

  list = applyOptionFilter(list, selectedLanguages, "languages");
  if (!list.length) return [];

  list = applyOptionFilter(list, selectedSubspecialties, "subspecialties");
  if (!list.length) return [];

  list = applyNetworkFilter(list, selectedNetworks);
  if (!list.length) return [];

  if (filters?.minimumRate) {
    const minimumRate = Number(filters.minimumRate);
    list = list.filter(item => item.ScoreTotal >= minimumRate);
  }
  if (!list.length) return [];

  return applyFacilitiesFilter(list, filters?.facilities ?? []);
}

export function sortAffiliateList(list, sort) {
  const sortedList = [...list].sort((a, b) => {
    let base = 0;

    if (sort === "" || sort === "Indicação Omint") {
      if (a.ScoreTotal < b.ScoreTotal) base = 100;
      if (a.ScoreTotal > b.ScoreTotal) base = -100;
    }

    if (a.Distancia === undefined && b.Distancia === undefined) return base + 3;
    if (a.Distancia === undefined && b.Distancia !== undefined) return base + 2;
    if (a.Distancia !== undefined && b.Distancia === undefined) return base - 2;
    if (a.Distancia < b.Distancia) return base - 1;
    if (a.Distancia > b.Distancia) return base + 1;
    return base;
  });

  if (sort !== "Rede e Distância") return sortedList;

  const principal = sortedList.filter(item => item.TpRede === "P" || item.TpRede === null);
  const complementar = sortedList.filter(item => item.TpRede === "C");
  const mediService = sortedList.filter(item => item.TpRede === "A");
  return [...principal, ...complementar, ...mediService];
}
